import { useCallback, useRef, useState } from 'react'
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react'
import { cssVars } from '../cssVars'
import styles from './DataTable.module.css'
import type { Column, ColumnAlign, SortDirection, SortState } from './types'

/**
 * The table.
 *
 * Almost every screen in this console is a list of records that somebody has to
 * be able to read, sort, select and act on, so this component carries more of
 * the product's usability than any other single file here.
 *
 * It is a real `<table>`. The div-and-flexbox grid that every dashboard toolkit
 * ships is easier to style and it destroys the only navigation a screen reader
 * user has in a table: there are no rows and columns to move between, no header
 * announced with each cell, and no way to ask what column you are in. Getting
 * `<table>` to do sticky headers and elastic columns took the CSS below, and
 * that is the cheaper half of the trade.
 *
 * Two things this deliberately does not do:
 *
 *  - It does not sort. Sortable columns raise `onSortChange` and the ordered
 *    page comes back from the server. Sorting in the browser would order the
 *    twenty-five rows in hand and present the result as the order of all four
 *    hundred, which is a wrong answer that looks exactly like a right one.
 *  - It is not `role="grid"`. A grid is a spreadsheet: the reading model becomes
 *    cell by cell and the keyboard contract obliges arrow navigation between
 *    cells and an editing mode inside them. These tables are lists of records,
 *    the row is the unit a person acts on, and native table semantics say so.
 */

export interface DataTableSelection<Row> {
  selectedIds: ReadonlySet<string>
  onChange(next: ReadonlySet<string>): void
  /**
   * The accessible name of one row's checkbox. Required, not optional, which is
   * the point of grouping selection into one object: there is no way to switch
   * selection on and leave four hundred checkboxes called "checkbox".
   */
  rowLabel(row: Row): string
  /**
   * The accessible name of the header checkbox, and it must say "on this page".
   * The box selects the rows currently loaded, never the whole result set, and
   * a label that leaves that out is how somebody closes four hundred cases
   * meaning to close twenty-five.
   */
  pageLabel: string
}

export interface DataTableProps<Row> {
  /**
   * What this table is a list of. Rendered as a real `<caption>`, hidden by
   * default because the page heading usually says it already, present always
   * because a screen reader landing inside the table has no page heading in
   * earshot.
   */
  caption: string
  captionVisible?: boolean
  columns: ReadonlyArray<Column<Row>>
  rows: readonly Row[]
  /** Stable identity, used for React keys, selection and keyboard focus. */
  getRowId(row: Row): string
  sort?: SortState | null
  onSortChange?(next: SortState): void
  selection?: DataTableSelection<Row>
  /** Opening a record. Fired by Enter on a focused row and by a plain click. */
  onRowActivate?(row: Row): void
  loading?: boolean
  /** Announced while loading. Kept a prop because this component holds no copy. */
  loadingLabel?: string
  skeletonRows?: number
  /** Shown when there are no rows. An `EmptyState` is the usual thing to pass. */
  empty?: ReactNode
  stickyHeader?: boolean
  density?: 'compact' | 'relaxed'
  className?: string
}

export function DataTable<Row>({
  caption,
  captionVisible = false,
  columns,
  rows,
  getRowId,
  sort,
  onSortChange,
  selection,
  onRowActivate,
  loading = false,
  loadingLabel,
  skeletonRows = 6,
  empty,
  stickyHeader = true,
  density,
  className,
}: DataTableProps<Row>) {
  const bodyRef = useRef<HTMLTableSectionElement | null>(null)

  /*
   * Focus is remembered by row id rather than by row index. Sorting rearranges
   * the array, and an index would leave the ring sitting on whichever record
   * happened to land in that position, which looks like the table moving the
   * selection by itself.
   */
  const [focusedId, setFocusedId] = useState<string | null>(null)

  const ids = rows.map(getRowId)
  const firstId = ids[0] ?? null
  const rovingId = focusedId !== null && ids.includes(focusedId) ? focusedId : firstId

  const selectedOnPage = selection ? ids.filter((id) => selection.selectedIds.has(id)) : []
  const allOnPageSelected = ids.length > 0 && selectedOnPage.length === ids.length
  const someOnPageSelected = selectedOnPage.length > 0 && !allOnPageSelected

  /*
   * `indeterminate` is a DOM property with no HTML attribute behind it, so React
   * cannot render it and a callback ref is the way in. `aria-checked="mixed"`
   * was the alternative and it is wrong twice over: it does not paint the dash,
   * and on a native checkbox it contradicts the implicit checked state that the
   * element already exposes.
   */
  const headerBoxRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (node) node.indeterminate = someOnPageSelected
    },
    [someOnPageSelected],
  )

  function toggleAllOnPage() {
    if (!selection) return
    const next = new Set(selection.selectedIds)
    // Adds and removes only this page's ids. Selection survives paging, so
    // replacing the set would silently drop rows the person chose two pages ago.
    for (const id of ids) {
      if (allOnPageSelected) next.delete(id)
      else next.add(id)
    }
    selection.onChange(next)
  }

  function toggleRow(id: string) {
    if (!selection) return
    const next = new Set(selection.selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    selection.onChange(next)
  }

  function sortDirectionFor(column: Column<Row>): SortDirection | 'none' | undefined {
    if (!column.sortable) return undefined
    if (sort && sort.key === column.key) return sort.direction
    return 'none'
  }

  function requestSort(column: Column<Row>) {
    if (!onSortChange) return
    const active = sort?.key === column.key
    onSortChange({
      key: column.key,
      // A second press on the active column reverses it. A first press on any
      // column starts ascending, always, so the result of a click is the same
      // whichever column was sorted before it.
      direction: active && sort?.direction === 'ascending' ? 'descending' : 'ascending',
    })
  }

  function focusRowAt(index: number) {
    const row = rows[index]
    const element = bodyRef.current?.rows[index]
    if (!row || !element) return
    setFocusedId(getRowId(row))
    element.focus()
  }

  function onRowKeyDown(event: KeyboardEvent<HTMLTableRowElement>, row: Row, index: number) {
    // Only when the row itself has focus. A button inside a cell owns its own
    // keys, and stealing Space from it would break every control in the table.
    if (event.target !== event.currentTarget) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        focusRowAt(index + 1)
        break
      case 'ArrowUp':
        event.preventDefault()
        focusRowAt(index - 1)
        break
      case 'Home':
        event.preventDefault()
        focusRowAt(0)
        break
      case 'End':
        event.preventDefault()
        focusRowAt(rows.length - 1)
        break
      case ' ':
        if (!selection) return
        // Without this the page scrolls under the person as they select.
        event.preventDefault()
        toggleRow(getRowId(row))
        break
      case 'Enter':
        if (!onRowActivate) return
        event.preventDefault()
        onRowActivate(row)
        break
      default:
        break
    }
  }

  function onRowClick(event: MouseEvent<HTMLTableRowElement>, row: Row) {
    setFocusedId(getRowId(row))
    if (!onRowActivate) return
    // A row that opens a record still holds buttons, links and checkboxes, and
    // a click on one of those is not a request to leave the page.
    if (event.target instanceof Element && event.target.closest('button, a, input, label')) return
    onRowActivate(row)
  }

  const columnCount = columns.length + (selection ? 1 : 0)

  return (
    <div
      className={[styles.frame, className].filter(Boolean).join(' ')}
      // Density is a token-level attribute, read by the rules already written in
      // tokens.css, so a compact table needs no CSS of its own here.
      data-density={density}
    >
      <div className={styles.scroll}>
        <table className={styles.table} aria-busy={loading || undefined}>
          <caption className={captionVisible ? styles.caption : 'visually-hidden'}>
            {caption}
          </caption>

          <thead className={stickyHeader ? styles.stickyHead : undefined}>
            <tr>
              {selection ? (
                <th scope="col" className={[styles.head, styles.selectCell].join(' ')}>
                  <input
                    type="checkbox"
                    ref={headerBoxRef}
                    className={styles.checkbox}
                    checked={allOnPageSelected}
                    onChange={toggleAllOnPage}
                    disabled={ids.length === 0}
                    aria-label={selection.pageLabel}
                  />
                </th>
              ) : null}

              {columns.map((column) => {
                const direction = sortDirectionFor(column)

                return (
                  <th
                    key={column.key}
                    scope="col"
                    className={[styles.head, alignClassOf(column)].filter(Boolean).join(' ')}
                    aria-sort={direction}
                    style={cssVars({ '--col-width': column.width })}
                  >
                    {column.sortable && onSortChange ? (
                      <button
                        type="button"
                        className={styles.sortButton}
                        onClick={() => requestSort(column)}
                      >
                        {/* The accessible name is the column name and nothing
                            else. Appending "sort ascending" would make the name
                            change with the state, so the header could no longer
                            be found by the name it had a moment earlier, and
                            `aria-sort` already carries the state properly. */}
                        <span>{column.header}</span>
                        <SortGlyph direction={direction === 'none' ? undefined : direction} />
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>

          {loading && rows.length === 0 ? (
            <tbody aria-hidden="true">
              {Array.from({ length: skeletonRows }, (_, index) => (
                <tr key={index} className={styles.row}>
                  {Array.from({ length: columnCount }, (_, cell) => (
                    <td key={cell} className={styles.cell}>
                      <span className={styles.skeleton} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ) : rows.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={columnCount} className={styles.emptyCell}>
                  {empty}
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody ref={bodyRef}>
              {rows.map((row, index) => {
                const id = ids[index] ?? ''
                const selected = selection?.selectedIds.has(id) ?? false

                return (
                  <tr
                    key={id}
                    className={styles.row}
                    data-selected={selected || undefined}
                    data-activatable={onRowActivate ? '' : undefined}
                    /*
                     * One tab stop for the whole table. Making every row a stop
                     * puts four hundred presses between the table and the
                     * pagination underneath it, which is a keyboard trap in
                     * everything but name.
                     */
                    tabIndex={id === rovingId ? 0 : -1}
                    onKeyDown={(event) => onRowKeyDown(event, row, index)}
                    onClick={(event) => onRowClick(event, row)}
                  >
                    {selection ? (
                      <td className={[styles.cell, styles.selectCell].join(' ')}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={selected}
                          onChange={() => toggleRow(id)}
                          aria-label={selection.rowLabel(row)}
                        />
                      </td>
                    ) : null}

                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={[styles.cell, alignClassOf(column)].filter(Boolean).join(' ')}
                      >
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          )}
        </table>
      </div>

      {/*
       * Announced once when a fetch starts. The skeleton rows above are
       * `aria-hidden`, so without this a screen reader is told only that the
       * table is busy and never that anything is coming.
       */}
      {loading && loadingLabel ? (
        <div className="visually-hidden" role="status">
          {loadingLabel}
        </div>
      ) : null}
    </div>
  )
}

/**
 * One place where the column's declared alignment becomes a class, so the
 * header cell and the body cell cannot disagree. They did in an earlier draft,
 * where the two ternaries were written out separately and only one of them knew
 * about `numeric`, which right-aligned a column of counts under a left-aligned
 * heading.
 */
function alignClassOf(column: { align?: ColumnAlign }): string | undefined {
  if (column.align === 'numeric') return styles.numeric
  if (column.align === 'end') return styles.end
  return undefined
}

/**
 * The sort indicator.
 *
 * Drawn here rather than imported from the shared icon set, because the data
 * layer has to compile on its own and this is the only glyph it needs. Same
 * rules as every other icon in the two applications: a 1.6 unit hairline on a
 * 24 unit grid, round caps, no fill, and no accessible name of its own, because
 * the state it depicts is already on the header's `aria-sort`.
 *
 * An unsorted column shows both chevrons rather than a hopeful upward one. A
 * single arrow on an unsorted column is read as the order the table is already
 * in, and then every sortable column claims to be the sorted one.
 */
function SortGlyph({ direction }: { direction?: SortDirection }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable={false}
      className={direction ? styles.sortGlyphActive : styles.sortGlyph}
    >
      {direction === undefined ? (
        <>
          <path d="M7 11l5-5 5 5" />
          <path d="M7 13l5 5 5-5" />
        </>
      ) : direction === 'ascending' ? (
        <path d="M6 14l6-6 6 6" />
      ) : (
        <path d="M6 10l6 6 6-6" />
      )}
    </svg>
  )
}
